import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/joy/Grid';
import Stack from '@mui/joy/Stack';
import Tab, { tabClasses } from '@mui/joy/Tab';
import TabList from '@mui/joy/TabList';
import TabPanel from '@mui/joy/TabPanel';
import Tabs from '@mui/joy/Tabs';
import { Typography, Modal } from '@mui/material';
import { TOSButton } from './style';
import { ColorsV2 } from 'app/constants/color';
import { Buttons } from 'app/constants/sizing';
import apiService from 'app/services/apiService';
import AddSectionToTOSButton from './AddSectionToTOSButton';
import AddToTOSButton from './AddToTOSButton';
import AddToTOSMenu from './AddToTOSMenu';
import GapSlider from './GapSlider';
import RecommendationButton from './RecommendationButton';
import TOSInvitationModal from './TOSInvitationModal';
import { Toast } from 'app/components';

const GapTabMenu = ({
  fwId,
  sections,
  onRecommend, // handleOpenRecommend -> onRecommend
  renderKey,
  onSliderChange, // onReRender -> onSliderChange -> onReRenderChartsAndRecommendations
  frameworkData,
  setFrameworkData,
  curSection,
  setCurSection
}) => {
  const language = JSON.parse(localStorage.getItem('lang'));
  const initialToast = {open: false, severity: '', message: ''};

  const [isQsLoading, setIsQsLoading] = useState(true);
  const [isAsLoading, setIsAsLoading] = useState(true);
  const [totalQsInFramework, setTotalQsInFramework] = useState(0);
  const [InvitationModalOpen, setInvitationModalOpen] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [toast, setToast] = useState(initialToast);

  const [nrOfQuestionsInBatch, setNrOfQuestionsInBatch] = useState(0);
  const [questionsInTOSBatch, setQuestionsInTOSBatch] = useState([]);
  const [resetCheckbox, setResetCheckbox] = useState(0);
  const [recommendedEmails, setRecommendedEmails] = useState([]);

  const fetchAllQuestions = async () => {
    setIsQsLoading(true);
    let res = null;
    try {
      res = await apiService.fetchAllQuestions(fwId, language);
      setFrameworkData(prevData => {
        // Merge all keys within the section-key from both the existing framework data and the response
        const mergedSections = [
          ...new Set([
            ...Object.keys(prevData?.[fwId]?.sections),
            ...Object.keys(res.data.sections),
          ]),
        ].reduce((result, key) => {
          result[key] = {
            ...(prevData?.[fwId]?.sections[key] || {}),
            ...(res.data.sections[key] || {}),
          };
          return result;
        }, {});
        return {
          ...prevData,
          [fwId]: {
            ...prevData[fwId],
            sections: mergedSections,
          },
        };
      });
      setIsQsLoading(false);
    } catch (error) {
      console.error(error);
      // Display error
    }
  };

  const fetchAllAnswers = async () => {
    setIsAsLoading(true);
    let res = null;
    try {
      res = await apiService.fetchAllAnswers(fwId, language);
      setFrameworkData(prevData => {
        // Find all email mentions in any tosRecipients field across all sections and questions
        const emails = Object.values(res.data.sections || {}).flatMap(section =>
          Object.values(section.answers || {}).flatMap(answer =>
            (answer.tosRecipients || []).map(recipient => recipient.email)
          )
        );

        // Take only unique emails to set as recommendations
        setRecommendedEmails(Array.from(new Set(emails)));

        // Merge all keys within the section-key from both the existing framework data and the response
        const mergedSections = [
          ...new Set([
            ...Object.keys(prevData?.[fwId]?.sections),
            ...Object.keys(res.data.sections),
          ]),
        ].reduce((result, key) => {
          result[key] = {
            ...(prevData?.[fwId]?.sections[key] || {}),
            ...(res.data.sections[key] || {}),
          };

          // Gather a list of unique emails from all questions with the section
          const prevRecipients = Array.from(
            new Set(
              Object.values(res?.data?.sections?.[key]?.answers || {}).flatMap(answer =>
                (answer.tosRecipients || []).map(recipient => recipient.email)
              )
            )
          );

          result[key].tosRecipientsInSection = prevRecipients;
          result[key].nrOfTOSRecipientsInSection = prevRecipients.length || 0;
          return result;
        }, {});
        return {
          ...prevData,
          [fwId]: {
            ...prevData[fwId],
            sections: mergedSections,
          },
        };
      });
      setIsAsLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    // Set first section as default
    const defaultSection = sections?.[0]?.id || '';

    // Fetch all questions and answers in framework
    if (fwId) {
      fetchAllQuestions();
      fetchAllAnswers();
    }

    setCurSection(defaultSection);
    if (sections.length === 0) return;

    let totalQs = 0;
    sections.map(section => {
      totalQs += section.nrOfQuestions;
    }) || 0;
    setTotalQsInFramework(totalQs);
  }, [fwId]);

  const handleChange = (_, value) => {
    setCurSection(value);
  };

  const handleOpenInvitationModal = () => {
    setInvitationModalOpen(true);
  };

  const handleCloseInvitationModal = () => {
    setInvitationModalOpen(false);
  };

  const handleOpenConfirmClear = () => {
    setConfirmClearOpen(true);
  };

  const handleCloseConfirmClear = () => {
    setConfirmClearOpen(false);
  };

  const addQuestionToTOSBatch = id => {
    if (!questionsInTOSBatch.includes(id)) {
      setQuestionsInTOSBatch(prevQuestions => [...prevQuestions, id]);
      setNrOfQuestionsInBatch(cur => cur + 1);
    }
  };

  const removeQuestionFromTOSBatch = id => {
    if (questionsInTOSBatch.includes(id)) {
      setQuestionsInTOSBatch(prevQuestions => prevQuestions.filter(item => item !== id));
      setNrOfQuestionsInBatch(cur => cur - 1);
    }
  };

  const clearSelectedQuestions = () => {
    setQuestionsInTOSBatch([]);
    setNrOfQuestionsInBatch(0);
    forceUpdateCheckboxes();
    setConfirmClearOpen(false);
  };

  const forceUpdateCheckboxes = () => {
    setResetCheckbox(prev => prev + 1);
  };

  const { t } = useTranslation('translation', { keyPrefix: 'gap_analysis' });

  return (
    <>
      <TOSInvitationModal
        open={InvitationModalOpen}
        handleClose={handleCloseInvitationModal}
        recommendedEmails={recommendedEmails}
        setRecommendedEmails={setRecommendedEmails}
        tosBatch={questionsInTOSBatch}
        clearSelectedQuestions={clearSelectedQuestions}
        setFrameworkData={setFrameworkData}
        toast={toast}
        setToast={setToast}
      />
      <Modal
        open={confirmClearOpen}
        onClose={handleCloseConfirmClear}
        sx={{
          color: ColorsV2.white,
        }}
      >
        <Stack
          direction='column'
          gap='10px'
          sx={{
            minWidth: '400px',
            backgroundColor: ColorsV2.secondary,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            padding: '20px',
            borderRadius: '4px',
          }}
        >
          <Typography>{t('tos_confirm_clear')}</Typography>
          <Stack direction='row' alignItems='center' gap='10px' justifyContent='flex-end'>
            <TOSButton
              variant='text'
              size='small'
              sx={{
                ...Buttons.baseStyle.primaryButtonLowPriority,
                minWidth: { sm: '60px', lg: '80px' },
                maxWidth: { lg: '180px', xl: '280px' },
                paddingRight: '10px',
                paddingLeft: '10px',
                alignSelf: 'flex-end',
              }}
              onClick={handleCloseConfirmClear}
            >
              {t('cancel')}
            </TOSButton>
            <TOSButton
              variant='text'
              style={{ justifySelf: 'right' }}
              size='small'
              onClick={clearSelectedQuestions}
              sx={{
                ...Buttons.baseStyle.primaryButtonHighPriority,
                minWidth: { sm: '60px', lg: '80px' },
                maxWidth: { lg: '180px', xl: '280px' },
                paddingRight: '10px',
                paddingLeft: '10px',
                alignSelf: 'flex-end',
              }}
            >
              {t('confirm')}
            </TOSButton>
          </Stack>
        </Stack>
      </Modal>

      <Stack direction='column' justifyContent='center' alignItems='center'></Stack>
      <Stack
        direction='row'
        sx={{
          width: '100%',
          gap: '40px',
          paddingLeft: '40px',
          alignItems: 'center',
        }}
      >
        <Typography
          variant='body1'
          align='center'
          sx={{
            color: ColorsV2.white,
          }}
        >
          {totalQsInFramework} {t('questions_in_framework')}
        </Typography>
        <AddToTOSMenu
          tosBatch={questionsInTOSBatch}
          nrOfQuestionsInBatch={nrOfQuestionsInBatch}
          handleOpenTOS={handleOpenInvitationModal}
          handleOpenConfirmClear={handleOpenConfirmClear}
        ></AddToTOSMenu>
      </Stack>
      <Tabs
        onChange={(event, value) => handleChange(event, value)}
        value={curSection}
        defaultValue={sections?.[0]?.id || ''} // Set to first set of questions within chosen framework
        orientation='vertical'
        sx={{
          height: { md: 450, xl: 750 },
          borderRadius: '4px',
          overflow: 'auto',
          bgcolor: ColorsV2.secondary,
          marginTop: '8px',
          marginBottom: '8px',
          borderColor: ColorsV2.outline,
          borderStyle: 'solid',
          borderWidth: '2px',
        }}
      >
        <TabList
          disableUnderline
          tabFlex={1}
          sx={{
            overflow: 'auto',
            minWidth: 300,
            [`& .${tabClasses.root}`]: {
              color: ColorsV2.white,
              fontSize: 'sm',
              fontWeight: 'lg',
              bgcolor: ColorsV2.primary,
              borderRight: 'solid 2px' + ColorsV2.outline,
              [`&[aria-selected="true"]`]: {
                bgcolor: ColorsV2.secondary,
                color: ColorsV2.white,
                borderRight: 'solid 2px transparent',
              },
              [`&:hover`]: {
                bgcolor: ColorsV2.secondary,
              },
              [`&.${tabClasses.focusVisible}`]: {
                outlineOffset: '-4px',
              },
            },
          }}
        >
          {/* Generate Tabs from sections*/}
          {Object.entries(frameworkData[fwId]?.sections)?.map && !isAsLoading ? (
            Object.values(frameworkData[fwId]?.sections)?.map(section => {
              if(!section.id) return null;
              return (
              <Tab
                key={fwId + section.id}
                indicatorInset
                indicatorPlacement='left'
                value={section.id}
                variant=''
                sx={{
                  flexGrow: 1,
                  width: 300,
                  minHeight: 100,
                  maxHeight: 100,
                  paddingTop: '4px',
                  paddingBottom: '4px',
                  borderBottom: 'solid 2px' + ColorsV2.outline,
                  overflow: 'auto',
                }}
              >
                <Stack
                  direction='row'
                  justifyContent='space-between'
                  alignItems='center'
                  width={'100%'}
                >
                  <Stack direction='column'>
                    <Typography variant='body2'>{section?.name}</Typography>
                    <Typography variant='caption' sx={{ color: ColorsV2.textInfo }}>
                      {section?.nrOfAnswered} /{' '}
                      {section?.nrOfQuestions} {t('questions_in_section')}
                    </Typography>
                  </Stack>
                  <Stack direction='row' alignItems='center'>
                    <RecommendationButton
                      renderKey={renderKey}
                      canRecommend={
                        frameworkData[fwId]?.sections?.[section.id]?.autoRecommend || false
                      }
                      fwId={fwId}
                      onRecommend={onRecommend}
                      section={section}
                      questions={frameworkData[fwId]?.sections?.[section.id]?.questions || []}
                    ></RecommendationButton>
                    <AddSectionToTOSButton
                      tosBatch={questionsInTOSBatch}
                      addQuestion={addQuestionToTOSBatch}
                      removeQuestion={removeQuestionFromTOSBatch}
                      resetCheckbox={resetCheckbox}
                      updateCheckboxes={forceUpdateCheckboxes}
                      questions={frameworkData[fwId]?.sections?.[section.id]?.questions || []}
                      prevRecipients={
                        frameworkData[fwId]?.sections?.[section.id]?.tosRecipientsInSection
                      }
                      nrOfRecipients={
                        frameworkData[fwId]?.sections?.[section.id]?.nrOfTOSRecipientsInSection
                      }
                    ></AddSectionToTOSButton>
                  </Stack>
                </Stack>
              </Tab>
            )})
          ) : (
            <></>
          )}
        </TabList>

        {/* Generate Panels from questions within the current section (Re-render on a new chosen section) */}
        <TabPanel
          value={curSection}
          sx={{
            color: ColorsV2.white,
            overflow: 'auto',
            borderLeft: 'solid 0px',
            borderColor: ColorsV2.outline,
          }}
        >
          <Grid container spacing={2} sx={{ flexgrow: 1 }}>
            {isQsLoading || isAsLoading ? (
              <>{t('loading')}</>
            ) : (
              <>
                {sections?.map ? (
                  sections.map(section => {
                    return section.id === curSection ? (
                      <Typography
                        variant='h6'
                        key={section.id}
                        sx={{
                          paddingBottom: '10px',
                          paddingTop: '10px',
                          borderBottom: '2px solid ' + ColorsV2.outline,
                          width: '100%',
                        }}
                      >
                        {section.description}
                      </Typography>
                    ) : (
                      <Fragment key={section.id}></Fragment>
                    );
                  })
                ) : (
                  <></>
                )}
                {frameworkData[fwId].sections?.[curSection]?.questions?.map((question, index) => {
                  return (
                    <Grid
                      item
                      key={question.id}
                      component='section'
                      sx={{
                        width: '100%',
                        borderBottom: 'solid 2px',
                        borderColor: ColorsV2.outline,
                        paddingBottom: 2,
                        paddingTop: 2,
                      }}
                    >
                      <Typography
                        variant='h6'
                        sx={{
                          paddingBottom: '10px',
                          paddingTop: '10px',
                          width: '100%',
                        }}
                      >
                        {index + 1}. {question.description}
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{
                          paddingBottom: '15px',
                          color: ColorsV2.textInfo,
                        }}
                      >
                        {question.text}
                      </Typography>
                      <Stack
                        direction='row'
                        alignItems='center'
                        spacing={4}
                        sx={{
                          bgcolor: ColorsV2.primary,
                          marginBottom: '10px',
                          marginTop: '2px',
                          borderRadius: '10px',
                          paddingTop: '5px',
                          paddingRight: '10px',
                          paddingLeft: '10px',
                          boxShadow:
                            'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px',
                        }}
                      >
                        {frameworkData[fwId]?.sections?.[curSection]?.answers?.[question.id] ? (
                          <GapSlider
                            answer={
                              frameworkData[fwId]?.sections?.[curSection]?.answers?.[question.id]
                            }
                            questionId={question.id}
                            onReRenderChartsAndRecommendations={onSliderChange} // onReRender -> onSliderChange -> onReRenderChartsAndRecommendations
                            setFrameworkData={setFrameworkData}
                            curSection={curSection}
                            fwId={fwId}
                          ></GapSlider>
                        ) : (
                          <></>
                        )}
                        <AddToTOSButton
                          tosBatch={questionsInTOSBatch}
                          addQuestion={addQuestionToTOSBatch}
                          removeQuestion={removeQuestionFromTOSBatch}
                          resetCheckbox={resetCheckbox}
                          questionId={question.id}
                          answer={
                            frameworkData[fwId]?.sections?.[curSection]?.answers?.[question.id] ||
                            null
                          }
                        ></AddToTOSButton>
                      </Stack>
                    </Grid>
                  );
                })}
              </>
            )}
          </Grid>
        </TabPanel>
      </Tabs>
      <Toast
        open={toast.open}
        setOpen={() => { setToast(() => initialToast)}}
        message={toast.message}
        severity={toast.severity}
      />
    </>
  );
};

export default GapTabMenu;
